import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import createIcon from '../../asset/icon/create.png';
import deleteIcon from '../../asset/icon/delete.png';
import toggleIcon from '../../asset/icon/toggle.png';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import HintSmallWrapper from '../../shared/component/headless/button/HintSmallWrapper';
import { PIInput } from '../../shared/component/headless/input/InputBody';
import { PROJECT_INFO_DATE_END_TEXT_MAX_LENGTH } from '../../shared/constants/limits';
import { DeviceType, Language } from '../../shared/enum/EnumRepository';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useLanguageStore } from '../../shared/store/languageStore';
import { formatProjectInfoDateRange, parseProjectInfoDateRange } from '../../shared/util/projectInfoDate';

interface ProjectInfoDateInputProps {
  value: string;
  onChange: (value: string) => void;
}

const createDateInputState = (value: string) => {
  const range = parseProjectInfoDateRange(value);
  return {
    start: range?.start ?? '',
    end: range && !range.isTextEnd ? range.end : '',
    endText: range?.isTextEnd ? range.end : '',
    isPeriod: Boolean(range?.end),
    isTextEnd: range?.isTextEnd ?? false,
  };
};

const ProjectInfoDateInput: React.FC<ProjectInfoDateInputProps> = ({ value, onChange }) => {
  const isMobile = useDeviceStore((s) => s.deviceType === DeviceType.MOBILE);
  const language = useLanguageStore((s) => s.language);
  const isKorean = language === Language.KR;
  const range = parseProjectInfoDateRange(value);
  const [dates, setDates] = useState(() => createDateInputState(value));
  const lastValue = useRef(value);
  const { start, end, endText, isPeriod, isTextEnd } = dates;
  const startLabel = isKorean ? '시작일' : 'Start date';
  const endLabel = isKorean ? '종료일' : 'End date';
  const addLabel = isKorean ? '기간 추가' : 'Add period';
  const removeLabel = isKorean ? '기간 삭제' : 'Remove period';
  const toggleLabel = isTextEnd
    ? (isKorean ? '날짜로 입력' : 'Enter a date')
    : (isKorean ? '텍스트로 입력' : 'Enter text');

  useEffect(() => {
    if (value === lastValue.current) return;
    lastValue.current = value;
    setDates(createDateInputState(value));
  }, [value]);

  const updateDates = (next: typeof dates, save = true) => {
    setDates(next);
    if (!save) return;
    const nextValue = formatProjectInfoDateRange({
      start: next.start,
      end: next.isPeriod ? (next.isTextEnd ? next.endText : next.end) : '',
      isTextEnd: next.isTextEnd,
    });
    lastValue.current = nextValue;
    onChange(nextValue);
  };

  const handleDateChange = (field: 'start' | 'end', event: React.ChangeEvent<HTMLInputElement>) => {
    updateDates({ ...dates, [field]: event.target.value }, event.currentTarget.validity.valid);
  };

  const handleDateBlur = (field: 'start' | 'end', event: React.FocusEvent<HTMLInputElement>) => {
    if (!event.currentTarget.validity.valid) {
      event.currentTarget.reportValidity();
      const previous = field === 'start' ? range?.start : (range?.isTextEnd ? '' : range?.end);
      setDates((current) => ({ ...current, [field]: previous ?? '' }));
    }
  };

  const handleToggle = () => {
    updateDates({
      ...dates,
      isTextEnd: !isTextEnd,
      end: isTextEnd && start && end && end < start ? '' : end,
    });
  };

  const handleRemovePeriod = () => {
    updateDates({ ...dates, isPeriod: false, isTextEnd: false, end: '', endText: '' });
  };

  return (
    <Container>
      <DateSection>
        <DateInput
          $isMobile={isMobile}
          type="date"
          value={start}
          max={isPeriod && !isTextEnd && end ? end : '9999-12-31'}
          onChange={(event) => handleDateChange('start', event)}
          onBlur={(event) => handleDateBlur('start', event)}
          aria-label={startLabel}
        />
        {isPeriod ? (
          <EndDateSection>
            <span>-</span>
            {isTextEnd ? (
              <TextInputWrapper $isMobile={isMobile}>
                <TextLength>{endText.length} / {PROJECT_INFO_DATE_END_TEXT_MAX_LENGTH}</TextLength>
                <PIInput
                  type="text"
                  value={endText}
                  maxLength={PROJECT_INFO_DATE_END_TEXT_MAX_LENGTH}
                  onChange={(event) => {
                    if (event.target.value.length > PROJECT_INFO_DATE_END_TEXT_MAX_LENGTH) return;
                    updateDates({ ...dates, endText: event.target.value });
                  }}
                  placeholder={isKorean ? '진행 중, 종료일 미정 등' : 'Ongoing, end date TBD, etc.'}
                  aria-label={isKorean ? '종료 문구' : 'End text'}
                />
              </TextInputWrapper>
            ) : (
              <DateInput
                $isMobile={isMobile}
                type="date"
                value={end}
                min={start || undefined}
                max="9999-12-31"
                onChange={(event) => handleDateChange('end', event)}
                onBlur={(event) => handleDateBlur('end', event)}
                aria-label={endLabel}
              />
            )}
            <ButtonGroup>
              <HintSmallWrapper content={toggleLabel}>
                <UniversalIconBtn
                  size="small"
                  border={false}
                  isTransparent
                  backgroundImg={toggleIcon}
                  onClick={handleToggle}
                  aria-label={toggleLabel}
                  aria-pressed={isTextEnd}
                />
              </HintSmallWrapper>
              <HintSmallWrapper content={removeLabel}>
                <UniversalIconBtn
                  size="small"
                  border={false}
                  isTransparent
                  backgroundImg={deleteIcon}
                  onClick={handleRemovePeriod}
                  aria-label={removeLabel}
                />
              </HintSmallWrapper>
            </ButtonGroup>
          </EndDateSection>
        ) : (
          <HintSmallWrapper content={addLabel}>
            <UniversalIconBtn
              size="small"
              border={false}
              isTransparent
              backgroundImg={createIcon}
              onClick={() => setDates({ ...dates, isPeriod: true })}
              aria-label={addLabel}
            />
          </HintSmallWrapper>
        )}
      </DateSection>
      {range === null && <PreviousValue>{value}</PreviousValue>}
    </Container>
  );
};

export default ProjectInfoDateInput;

const Container = styled.div`
  width: 100%;
  min-width: 0;
`;

const DateSection = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: nowrap;
  min-width: 0;
  gap: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const DateInput = styled(PIInput)<{ $isMobile: boolean }>`
  width: ${({ $isMobile }) => $isMobile ? '150px' : 'clamp(120px, 15vw, 225px)'};
  flex-shrink: 1;
  max-width: 100%;
  min-width: 0;

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
  }
`;

const EndDateSection = styled.div`
  display: flex;
  align-items: baseline;
  flex: 0 1 auto;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
`;

const TextInputWrapper = styled.div<{ $isMobile: boolean }>`
  position: relative;
  width: ${({ $isMobile }) => $isMobile ? 'clamp(150px, 24vw, 300px)' : 'clamp(150px, 32vw, 600px)'};
  flex-shrink: 1;
  min-width: 0;
`;

const TextLength = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const PreviousValue = styled.div`
  padding-top: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;
